import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getFooter, STRAPI_URL } from '../services/api';
import { useLang } from '../context/LanguageContext';

const ELIT_ARGE_LOGO = '/elitarge-logo.webp';

const DEFAULT_FOOTER = [{
  id: 'def-footer',
  marka: 'RUZY',
  adres: 'Levent, Büyükdere Cd. No:199, 34394 Şişli/İstanbulLL',
  numara: '+90 (212) 000 09 00',
  mail: 'info@ruzy.com',
  logo: null
}];

const Footer = () => {
  const { t } = useTranslation();
  const [footerList, setFooterList] = useState([]);
  const { lang } = useLang(); 
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await getFooter();
        if (data && Array.isArray(data) && data.length > 0) {
          const mapped = data.map((item) => ({
            id: item.id,
            marka: item.marka ?? item.attributes?.marka ?? null,
            adres: item.adres ?? item.attributes?.adres ?? null,
            numara: item.numara ?? item.attributes?.numara ?? null,
            mail: item.mail ?? item.attributes?.mail ?? null,
            logo: item.logo ?? item.attributes?.logo ?? null,
            markaHak: item.markaHak ?? item.attributes?.markaHak ?? null,
          }));
          setFooterList(mapped);
        }
      } catch (error) {
        console.error('Footer veri çekme hatası:', error);
      }
    };
    fetchData();
  }, []);

  const formatList = (text) => {
    if (!text) return [];
    return text.split(/[,\n]+/).map((item) => item.trim()).filter((item) => item !== '');
  };

  const getLogoUrl = (logo) => {
    if (!logo) return null;
    const logoItem = Array.isArray(logo) ? logo[0] : logo;
    if (!logoItem) return null;
    const url = logoItem?.url ?? null;
    if (!url) return null;
    return url.startsWith('http') ? url : `${STRAPI_URL}${url}`;
  };

  const mainEntry = footerList[0] ?? DEFAULT_FOOTER[0];

  return (
    <footer className="bg-black text-white w-full pt-10 pb-5 font-poppins">
      <div className="max-w-6xl mx-auto px-5 flex flex-col items-center">

        {/* Logo + Marka */}
        <div className="flex flex-col items-center mb-12 w-full">
          {getLogoUrl(mainEntry.logo) ? (
            <img src={getLogoUrl(mainEntry.logo)} alt="RUZY footer logosu" className="max-w-[80px] h-auto mb-2.5 block" loading="lazy" />
          ) : (
            <div className="w-12 h-12 bg-white/10 rounded-full mb-3 flex items-center justify-center">
              <span className="text-xl font-bold">R</span>
            </div>
          )}
          <span className="text-2xl sm:text-3xl md:text-[42px] font-medium tracking-[3px] sm:tracking-[6px] text-white text-center uppercase">
            {mainEntry.marka || 'RUZY'}
          </span>
        </div>

        {/* Adres + İletişim */}
        <div className="flex flex-col md:flex-row justify-between items-start w-full max-w-4xl mx-auto px-4 md:px-12 mb-14 gap-10">
          <div className="flex flex-col max-w-[350px] w-full">
            <h4 className="text-lg font-bold mb-5 border-b border-white/10 pb-2">
              {t('footer.address') || 'Adres'}
            </h4>
            <div className="flex flex-col w-full gap-0.5">
              {footerList.map((entry, entryIndex) =>
                formatList(entry.adres).map((item, index, array) => (
                  <React.Fragment key={`addr-${entry.id}-${index}`}>
                    <address className="not-italic text-sm leading-relaxed break-words block m-0 py-0.5 opacity-80">
                      {item}
                    </address>
                    {(index !== array.length - 1 || entryIndex !== footerList.length - 1) && (
                      <hr className="border-0 border-t border-white/15 w-full my-2" />
                    )}
                  </React.Fragment>
                ))
              )}
            </div>
          </div>

          <div className="flex flex-col max-w-[350px] w-full">
            <h4 className="text-lg font-bold mb-5 border-b border-white/10 pb-2">
              {t('footer.contact') || 'İletişim'}
            </h4>
            <div className="flex flex-col w-full gap-2">
              {footerList.map((entry) =>
                formatList(entry.numara).map((telNum, idx) => (
                  <a key={`tel-${entry.id}-${idx}`} href={`numara:${telNum.replace(/\s+/g, '')}`} className="flex items-center gap-2 text-sm text-white no-underline hover:text-[#4BA18C] transition-colors">
                    <span className="material-symbols-outlined text-base">phone</span>
                    {telNum}
                  </a>
                ))
              )}
              {footerList.map((entry) =>
                formatList(entry.mail).map((mailAddr, idx) => (
                  <a key={`mail-${entry.id}-${idx}`} href={`mailto:${mailAddr}`} className="flex items-center gap-2 text-sm text-white no-underline hover:text-[#4BA18C] transition-colors">
                    <span className="material-symbols-outlined text-base">mail</span>
                    {mailAddr}
                  </a>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Elit Arge Bandı */}
        <div className="w-full max-w-xl mx-auto px-4 md:px-12 mb-8">
          <div className="flex items-center justify-center gap-4 bg-white/5 border border-white/10 rounded-2xl px-6 py-4">
            <img
              src={ELIT_ARGE_LOGO}
              alt="Elit Arge"
              className="h-10 w-auto object-contain"
              loading="lazy"
            />
            <div className="h-8 w-px bg-white/20" />
            <div className="flex flex-col">
              <span className="text-xs text-white/50 uppercase tracking-widest font-semibold">
                Powered by
              </span>
              <span className="text-sm text-white font-bold leading-tight">
                {lang === 'en' ? 'RUZY, a product of Elit Arge.' : 'RUZY, bir Elit Arge ürünüdür.'}
              </span>
            </div>
          </div>
        </div>

        {/* Copyright */}
        <div className="w-full text-center">
          <hr className="border-0 border-t border-white/20 w-full mb-5" />
          <p className="text-xs text-[#cfcfcf] opacity-60">
            © {new Date().getFullYear()} {mainEntry.marka || 'RUZY'}. {mainEntry.markaHak || t('footer.rights')}
          </p>
        </div>

      </div>
    </footer>
  );
};

export default Footer;